import type { CourseProgress } from "@/utils/storage/courseProgressStorage";
import type { VideoProgress } from "@/utils/storage/videoProgressStorage";
import { createContext, useContext } from "react";

export interface StorageState {
	topicSubscription: boolean | null;
	courseProgress: CourseProgress;
	videoProgress: VideoProgress;
	isLoading: boolean;
}

export interface StorageContextValue extends StorageState {
	setTopicSubscriptionValue: (value: boolean) => Promise<void>;
	setCourseProgressValue: (progress: CourseProgress) => Promise<void>;
	markCourseAsCompleted: (
		workshopId: string,
		courseClassesId: string
	) => Promise<void>;
	markCourseAsIncomplete: (
		workshopId: string,
		courseClassesId: string
	) => Promise<void>;
	isCourseCompleted: (workshopId: string, courseClassesId: string) => boolean;
	getWorkshopProgress: (workshopId: string) => {
		[courseClassesId: string]: boolean;
	};
	setVideoProgressValue: (progress: VideoProgress) => Promise<void>;
	saveVideoProgress: (
		workshopId: string,
		courseClassesId: string,
		currentTime: number,
		duration: number
	) => Promise<void>;
	getVideoProgressForCourse: (
		workshopId: string,
		courseClassesId: string
	) => { currentTime: number; duration: number; lastSaved: number } | null;
  clearVideoProgressForCourse: (workshopId: string, courseClassesId: string) => Promise<void>;
	shouldResumeFromSavedPosition: (
		workshopId: string,
		courseClassesId: string,
		videoDuration: number
	) => number | null;
	refreshStorageState: () => Promise<void>;
}

export const StorageContext = createContext<StorageContextValue | undefined>(
	undefined
);

export const useStorage = (): StorageContextValue => {
	const context = useContext(StorageContext);
	if (context === undefined) {
		throw new Error("useStorage must be used within a StorageProvider");
	}
	return context;
};

export const useTopicSubscription = (): boolean | null => {
	const { topicSubscription } = useStorage();
	return topicSubscription;
};

export const useCourseProgress = (): {
	progress: CourseProgress;
	markAsCompleted: (
		workshopId: string,
		courseClassesId: string
	) => Promise<void>;
	markAsIncomplete: (
		workshopId: string,
		courseClassesId: string
	) => Promise<void>;
	isCompleted: (workshopId: string, courseClassesId: string) => boolean;
	getWorkshopProgress: (workshopId: string) => {
		[courseClassesId: string]: boolean;
	};
} => {
	const {
		courseProgress,
		markCourseAsCompleted,
		markCourseAsIncomplete,
		isCourseCompleted,
		getWorkshopProgress,
	} = useStorage();

	return {
		progress: courseProgress,
		markAsCompleted: markCourseAsCompleted,
		markAsIncomplete: markCourseAsIncomplete,
		isCompleted: isCourseCompleted,
		getWorkshopProgress,
	};
};

export const useVideoProgress = (): {
	saveVideoProgress: (
		workshopId: string,
		courseClassesId: string,
		currentTime: number,
		duration: number
	) => Promise<void>;
	getVideoProgress: (
		workshopId: string,
		courseClassesId: string
	) => { currentTime: number; duration: number; lastSaved: number } | null;
	getAllVideoProgress: () => VideoProgress;
  clearVideoProgress: (workshopId: string, courseClassesId: string) => Promise<void>;
	shouldResumeFromSavedPosition: (
		workshopId: string,
		courseClassesId: string,
		videoDuration: number
	) => number | null;
} => {
	const {
		videoProgress,
		saveVideoProgress,
		getVideoProgressForCourse,
    clearVideoProgressForCourse,
		shouldResumeFromSavedPosition,
	} = useStorage();

	return {
		saveVideoProgress,
		getVideoProgress: getVideoProgressForCourse,
		getAllVideoProgress: () => videoProgress,
    clearVideoProgress: clearVideoProgressForCourse,
		shouldResumeFromSavedPosition,
	};
};
